/**
 * Cap Table screen — shareholder & share-class management. Investor-
 * facing data, so the screen is conservative: inline form for adds and
 * edits (no extra modal layer to surprise the operator), explicit
 * confirm dialogs on delete + email-broadcast, audit-trail reason
 * field exposed on every share-count update.
 *
 * Five tabs: Overview / Shareholders / Transactions / Announcements /
 * Documents. The legacy app.nurev.io view also has a slide-in
 * shareholder detail drawer with "regenerate invite link"; that path
 * is intentionally deferred — clicking a shareholder name in v1 just
 * scrolls them into the edit form. The full drawer becomes a separate
 * port when regenerate-link is actually needed in the admin workflow.
 *
 * Per the cap-table-source memory note: this surface IS the source of
 * truth. Never fall back to local CSVs.
 */
import { FormEvent, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  AnnouncementCategory,
  ShareTransaction,
  Shareholder,
  ShareholderDocument
} from '@/api/capTable';
import {
  useAdminAnnouncements,
  useCapTableSummary,
  useCreateAnnouncement,
  useCreateDocument,
  useCreateShareholder,
  useDeleteAnnouncement,
  useDeleteDocument,
  useDeleteShareholder,
  useInviteShareholder,
  useShareConfig,
  useShareTransactions,
  useShareholderDocuments,
  useShareholders,
  useUpdateAnnouncement,
  useUpdateShareConfig,
  useUpdateShareholder
} from '@/hooks/useCapTable';
import {
  ActionButton,
  Card,
  Pagination,
  SectionLabel,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtNum } from '@/lib/format';

const pct = (n?: number | null): string =>
  n != null && Number.isFinite(n) ? `${Number(n).toFixed(2)}%` : '—';

const INVITE_BADGE: Record<string, string> = {
  pending: 'bg-white/10 text-nurev-muted ring-1 ring-white/10',
  sent: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  accepted: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30'
};

const CATEGORIES: Array<{
  id: AnnouncementCategory;
  label: string;
  badge: string;
}> = [
  {
    id: 'milestone',
    label: 'Milestone',
    badge: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30'
  },
  {
    id: 'product',
    label: 'Product',
    badge: 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30'
  },
  {
    id: 'fundraising',
    label: 'Fundraising',
    badge: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30'
  },
  {
    id: 'team',
    label: 'Team',
    badge: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30'
  },
  {
    id: 'general',
    label: 'General',
    badge: 'bg-white/10 text-nurev-muted ring-1 ring-white/10'
  }
];
const catLabel = (id?: string) =>
  CATEGORIES.find(c => c.id === id)?.label || 'General';
const catBadge = (id?: string) =>
  CATEGORIES.find(c => c.id === id)?.badge || CATEGORIES[4].badge;

// ─── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab() {
  const { data: summary, isLoading } = useCapTableSummary();
  const { data: cfg } = useShareConfig();
  const update = useUpdateShareConfig();

  const [editing, setEditing] = useState(false);
  const [classA, setClassA] = useState('');
  const [classB, setClassB] = useState('');
  const [status, setStatus] = useState<string | null>(null);

  const startEdit = () => {
    setClassA(String(cfg?.classATotalShares || 0));
    setClassB(String(cfg?.classBTotalShares || 0));
    setStatus(null);
    setEditing(true);
  };

  const save = async () => {
    setStatus(null);
    try {
      await update.mutateAsync({
        classATotalShares: parseInt(classA) || 0,
        classBTotalShares: parseInt(classB) || 0
      });
      setStatus('Share totals updated.');
      setEditing(false);
    } catch (err) {
      setStatus(
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Update failed.'
      );
    }
  };

  const a = summary?.classA;
  const b = summary?.classB;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          label="Total Shareholders"
          value={fmtNum(summary?.totalShareholders)}
          loading={isLoading}
        />
        {/*
          Equity Shares (Class B) is the headline. Class A is voting
          control (typically 100 shares, held by the founder) and is
          not part of the equity pool — surfacing the sum
          (classA.total + classB.total) misleadingly displays the cap
          table as e.g. 11,000,100 instead of the equity pool of
          11,000,000. The voting block is already visible in the
          Voting (Board Members) config card below + on each
          shareholder row.
        */}
        <StatCard
          label="Equity Shares"
          value={fmtNum(b?.total)}
          sub={`${fmtNum(b?.allocated)} allocated`}
          loading={isLoading}
        />
        <StatCard
          label="Portal Users"
          value={fmtNum(summary?.activePortalUsers)}
          sub={`${fmtNum(summary?.invitedCount)} invited`}
          loading={isLoading}
        />
        <StatCard
          label="Unallocated"
          value={fmtNum((a?.unallocated || 0) + (b?.unallocated || 0))}
          loading={isLoading}
        />
      </div>

      <Card>
        <div className="mb-4 flex items-center justify-between">
          <SectionLabel>Share Classes</SectionLabel>
          {!editing && (
            <button
              onClick={startEdit}
              className="text-xs text-amber-300 hover:text-amber-200"
            >
              Edit totals
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ShareClassPanel
            cls="A"
            label="Voting (Board Members)"
            badge="bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30"
            barColor="bg-purple-500"
            data={a}
            editing={editing}
            value={classA}
            onValueChange={setClassA}
          />
          <ShareClassPanel
            cls="B"
            label="Non-Voting Equity"
            badge="bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30"
            barColor="bg-emerald-500"
            data={b}
            editing={editing}
            value={classB}
            onValueChange={setClassB}
          />
        </div>

        {editing && (
          <>
            {status && <p className="mt-3 text-xs text-amber-300">{status}</p>}
            <div className="mt-4 flex gap-2">
              <ActionButton
                size="sm"
                onClick={save}
                disabled={update.isPending}
              >
                {update.isPending ? 'Saving…' : 'Save'}
              </ActionButton>
              <ActionButton
                size="sm"
                variant="neutral"
                onClick={() => setEditing(false)}
              >
                Cancel
              </ActionButton>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function ShareClassPanel({
  cls,
  label,
  badge,
  barColor,
  data,
  editing,
  value,
  onValueChange
}: {
  cls: 'A' | 'B';
  label: string;
  badge: string;
  barColor: string;
  data?: { total: number; allocated: number; unallocated: number };
  editing: boolean;
  value: string;
  onValueChange: (v: string) => void;
}) {
  return (
    <div className="rounded-lg bg-white/[0.03] p-4">
      <div className="mb-3 flex items-center gap-2">
        <span
          className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${badge}`}
        >
          Class {cls}
        </span>
        <span className="text-xs text-nurev-muted">{label}</span>
      </div>
      {editing ? (
        <div className="space-y-2">
          <label className="text-[10px] uppercase text-nurev-muted">
            Total shares
          </label>
          <input
            type="number"
            value={value}
            onChange={e => onValueChange(e.target.value)}
            className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 font-mono text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
          />
        </div>
      ) : (
        <>
          <p className="text-xl font-bold text-nurev-text">
            {fmtNum(data?.total)}
          </p>
          <div className="mt-2 flex justify-between text-xs text-nurev-muted">
            <span>Allocated: {fmtNum(data?.allocated)}</span>
            <span>Available: {fmtNum(data?.unallocated)}</span>
          </div>
          {data?.total && data.total > 0 && (
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/[0.06]">
              <div
                className={`h-full rounded-full ${barColor} transition-all`}
                style={{
                  width: `${(data.allocated / data.total) * 100}%`
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Shareholders tab ─────────────────────────────────────────────────────

type ShareholderSortKey =
  | 'name'
  | 'email'
  | 'classAShares'
  | 'classBShares'
  | 'totalShares'
  | 'fullyDilutedPercent'
  | 'inviteStatus';

function ShareholdersTab() {
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 250;
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<ShareholderSortKey>('totalShares');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [editId, setEditId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    classAShares: '',
    classBShares: '',
    notes: '',
    reason: ''
  });
  const [status, setStatus] = useState<{
    kind: 'ok' | 'err';
    text: string;
  } | null>(null);

  const { data, isLoading } = useShareholders({ page, limit: PAGE_SIZE });
  const create = useCreateShareholder();
  const update = useUpdateShareholder();
  const del = useDeleteShareholder();
  const invite = useInviteShareholder();

  const rawDocs = data?.docs || [];
  const total = data?.totalDocs || rawDocs.length;
  const totalPages = data?.totalPages || 1;

  const docs = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? rawDocs.filter(
          s =>
            (s.name || '').toLowerCase().includes(q) ||
            (s.email || '').toLowerCase().includes(q)
        )
      : rawDocs;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const na = Number(av);
      const nb = Number(bv);
      return sortDir === 'asc' ? na - nb : nb - na;
    });
  }, [rawDocs, query, sortKey, sortDir]);

  const onSort = (key: ShareholderSortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      const asc: ShareholderSortKey[] = ['name', 'email', 'inviteStatus'];
      setSortDir(asc.includes(key) ? 'asc' : 'desc');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      classAShares: '',
      classBShares: '',
      notes: '',
      reason: ''
    });
    setShowAdd(false);
    setEditId(null);
    setStatus(null);
  };

  const startEdit = (s: Shareholder) => {
    setEditId(s._id);
    setShowAdd(false);
    setForm({
      name: s.name,
      email: s.email,
      classAShares: String(s.classAShares),
      classBShares: String(s.classBShares),
      notes: s.notes || '',
      reason: ''
    });
    setStatus(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus(null);
    if (!form.name || !form.email) {
      setStatus({ kind: 'err', text: 'Name and email required.' });
      return;
    }
    try {
      if (editId) {
        await update.mutateAsync({
          id: editId,
          name: form.name,
          email: form.email,
          classAShares: parseInt(form.classAShares) || 0,
          classBShares: parseInt(form.classBShares) || 0,
          notes: form.notes,
          reason: form.reason
        });
      } else {
        await create.mutateAsync({
          name: form.name,
          email: form.email,
          classAShares: parseInt(form.classAShares) || 0,
          classBShares: parseInt(form.classBShares) || 0,
          notes: form.notes
        });
      }
      resetForm();
    } catch (err) {
      setStatus({
        kind: 'err',
        text:
          (err as { response?: { data?: { error?: string } } })?.response?.data
            ?.error || 'Save failed.'
      });
    }
  };

  const onDelete = async (s: Shareholder) => {
    if (
      !window.confirm(
        `Remove ${s.name}? This also deletes their portal account.`
      )
    ) {
      return;
    }
    try {
      await del.mutateAsync(s._id);
    } catch (err) {
      window.alert(
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Delete failed.'
      );
    }
  };

  const onInvite = async (s: Shareholder) => {
    try {
      const res = await invite.mutateAsync(s._id);
      // Always confirm. This used to only alert when res.tempPassword was
      // set — a field the API never returns — so every invite/resend
      // succeeded silently and looked like nothing happened.
      window.alert(
        [
          `Invite email sent to ${s.email}.`,
          'The link expires in 14 days.',
          res?.inviteUrl
            ? `\nIf the email doesn't arrive, share this link directly:\n${res.inviteUrl}`
            : ''
        ]
          .filter(Boolean)
          .join('\n')
      );
    } catch (err) {
      window.alert(
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Invite failed.'
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search name or email…"
            className="w-64 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-xs text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
          />
          <p className="text-xs text-nurev-muted">
            {query
              ? `${docs.length} of ${total} shareholder${docs.length === 1 ? '' : 's'}`
              : `${total} shareholders`}
          </p>
        </div>
        <ActionButton
          size="sm"
          onClick={() => {
            resetForm();
            setShowAdd(true);
          }}
        >
          + Add Shareholder
        </ActionButton>
      </div>

      {(showAdd || editId) && (
        <Card className="border-amber-500/30">
          <form onSubmit={submit} className="space-y-3">
            <SectionLabel>
              {editId ? 'Edit Shareholder' : 'New Shareholder'}
            </SectionLabel>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Name">
                <Input
                  value={form.name}
                  onChange={v => setForm({ ...form, name: v })}
                />
              </Field>
              <Field label="Email">
                <Input
                  value={form.email}
                  type="email"
                  onChange={v => setForm({ ...form, email: v })}
                />
              </Field>
              <Field label="Class A Shares (Voting)">
                <Input
                  value={form.classAShares}
                  type="number"
                  mono
                  onChange={v => setForm({ ...form, classAShares: v })}
                />
              </Field>
              <Field label="Class B Shares (Non-Voting)">
                <Input
                  value={form.classBShares}
                  type="number"
                  mono
                  onChange={v => setForm({ ...form, classBShares: v })}
                />
              </Field>
              {editId && (
                <div className="md:col-span-2">
                  <Field label="Reason for change (audit-logged)">
                    <Input
                      value={form.reason}
                      placeholder="e.g. Series A allocation"
                      onChange={v => setForm({ ...form, reason: v })}
                    />
                  </Field>
                </div>
              )}
              <div className="md:col-span-2">
                <Field label="Notes">
                  <Input
                    value={form.notes}
                    onChange={v => setForm({ ...form, notes: v })}
                  />
                </Field>
              </div>
            </div>
            {status && (
              <p
                className={`text-xs ${
                  status.kind === 'ok' ? 'text-emerald-300' : 'text-red-300'
                }`}
              >
                {status.text}
              </p>
            )}
            <div className="flex gap-2">
              <ActionButton
                type="submit"
                size="sm"
                disabled={create.isPending || update.isPending}
              >
                {create.isPending || update.isPending
                  ? 'Saving…'
                  : editId
                    ? 'Update'
                    : 'Add'}
              </ActionButton>
              <ActionButton size="sm" variant="neutral" onClick={resetForm}>
                Cancel
              </ActionButton>
            </div>
          </form>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-nurev-muted">
                <SortHeader
                  active={sortKey === 'name'}
                  dir={sortDir}
                  label="Name"
                  onClick={() => onSort('name')}
                />
                <SortHeader
                  active={sortKey === 'classAShares'}
                  dir={sortDir}
                  label="Class A"
                  align="right"
                  onClick={() => onSort('classAShares')}
                />
                <SortHeader
                  active={sortKey === 'classBShares'}
                  dir={sortDir}
                  label="Class B"
                  align="right"
                  onClick={() => onSort('classBShares')}
                />
                <SortHeader
                  active={sortKey === 'totalShares'}
                  dir={sortDir}
                  label="Total"
                  align="right"
                  onClick={() => onSort('totalShares')}
                />
                <SortHeader
                  active={sortKey === 'fullyDilutedPercent'}
                  dir={sortDir}
                  label="Ownership"
                  align="right"
                  onClick={() => onSort('fullyDilutedPercent')}
                />
                <SortHeader
                  active={sortKey === 'inviteStatus'}
                  dir={sortDir}
                  label="Portal"
                  onClick={() => onSort('inviteStatus')}
                />
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-nurev-border">
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-4 py-3">
                      <Skeleton className="h-5 w-full" />
                    </td>
                  </tr>
                ))
              ) : docs.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-nurev-muted"
                  >
                    No shareholders yet.
                  </td>
                </tr>
              ) : (
                docs.map(s => (
                  <ShareholderRow
                    key={s._id}
                    s={s}
                    onEdit={() => startEdit(s)}
                    onInvite={() => onInvite(s)}
                    onDelete={() => onDelete(s)}
                    invitePending={invite.isPending}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="border-t border-nurev-border px-4 py-3">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}

function ShareholderRow({
  s,
  onEdit,
  onInvite,
  onDelete,
  invitePending
}: {
  s: Shareholder;
  onEdit: () => void;
  onInvite: () => void;
  onDelete: () => void;
  invitePending: boolean;
}) {
  return (
    <tr className="transition-colors hover:bg-white/[0.02]">
      <td className="px-4 py-2">
        <button onClick={onEdit} className="group text-left">
          <div className="flex items-center gap-2">
            <p className="font-medium text-nurev-text transition-colors group-hover:text-amber-200">
              {s.name}
            </p>
            {s.isResidual && (
              <span
                title="Class B auto-rebalances to absorb the remainder of the pool"
                className="inline-block rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-300 ring-1 ring-amber-500/30"
              >
                Residual
              </span>
            )}
          </div>
          <p className="text-[10px] text-nurev-muted">{s.email}</p>
        </button>
      </td>
      <td className="px-4 py-2 text-right font-mono text-nurev-text">
        {fmtNum(s.classAShares)}
      </td>
      <td className="px-4 py-2 text-right font-mono text-nurev-text">
        {fmtNum(s.classBShares)}
      </td>
      <td className="px-4 py-2 text-right font-mono font-bold text-nurev-text">
        {fmtNum(s.totalShares)}
      </td>
      <td className="px-4 py-2 text-right font-mono text-emerald-300">
        {pct(s.fullyDilutedPercent)}
      </td>
      <td className="px-4 py-2">
        <span
          className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${
            INVITE_BADGE[s.inviteStatus] ||
            'bg-white/10 text-nurev-muted ring-1 ring-white/10'
          }`}
        >
          {s.inviteStatus}
        </span>
      </td>
      <td className="px-4 py-2">
        <div className="flex justify-end gap-2">
          <button
            onClick={onEdit}
            className="text-[10px] text-amber-300 hover:text-amber-200"
          >
            edit
          </button>
          {/* Invites expire after 14 days, so a 'sent' shareholder who never
              finished needs a fresh one. Previously only 'pending' showed a
              button, which meant the only way to re-invite was in-pod. */}
          {(s.inviteStatus === 'pending' || s.inviteStatus === 'sent') && (
            <button
              onClick={onInvite}
              disabled={invitePending}
              title={
                s.inviteStatus === 'sent'
                  ? 'Send a fresh invite email (previous link may have expired)'
                  : 'Send the portal invite email'
              }
              className="text-[10px] text-emerald-300 hover:text-emerald-200 disabled:opacity-50"
            >
              {s.inviteStatus === 'sent' ? 'resend' : 'invite'}
            </button>
          )}
          <button
            onClick={onDelete}
            className="text-[10px] text-red-300 hover:text-red-200"
          >
            remove
          </button>
        </div>
      </td>
    </tr>
  );
}

function SortHeader({
  label,
  active,
  dir,
  align = 'left',
  onClick
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
  onClick: () => void;
}) {
  return (
    <th
      onClick={onClick}
      className={`cursor-pointer select-none px-4 py-3 font-medium transition-colors hover:text-nurev-text ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${active ? 'text-amber-300' : ''}`}
    >
      {label}
      <span className="ml-1 inline-block w-2.5 text-[9px]">
        {active ? (dir === 'asc' ? '▲' : '▼') : ''}
      </span>
    </th>
  );
}

// ─── Transactions tab ─────────────────────────────────────────────────────

function TransactionsTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useShareTransactions({ page, limit: 25 });
  const docs = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-nurev-border px-5 py-3">
        <SectionLabel>Transaction Log</SectionLabel>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Shareholder</th>
              <th className="px-4 py-3 font-medium">Class</th>
              <th className="px-4 py-3 text-right font-medium">Change</th>
              <th className="px-4 py-3 text-right font-medium">New Total</th>
              <th className="px-4 py-3 font-medium">Reason</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-nurev-border/50">
                  <td colSpan={6} className="px-4 py-3">
                    <Skeleton className="h-5 w-full" />
                  </td>
                </tr>
              ))
            ) : docs.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-12 text-center text-nurev-muted"
                >
                  No transactions yet.
                </td>
              </tr>
            ) : (
              docs.map(t => <TransactionRow key={t._id} t={t} />)
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="border-t border-nurev-border px-4 py-3">
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </Card>
  );
}

function TransactionRow({ t }: { t: ShareTransaction }) {
  const positive = t.change > 0;
  return (
    <tr className="border-b border-nurev-border/50 hover:bg-white/[0.02]">
      <td className="px-4 py-2 text-xs text-nurev-muted">
        {fmtDate(t.createdAt)}
      </td>
      <td className="px-4 py-2 text-xs text-nurev-text">
        {t.shareholderName}
      </td>
      <td className="px-4 py-2">
        <span
          className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${
            t.shareClass === 'A'
              ? 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30'
              : 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30'
          }`}
        >
          Class {t.shareClass}
        </span>
      </td>
      <td
        className={`px-4 py-2 text-right font-mono text-xs ${
          positive ? 'text-emerald-300' : 'text-red-300'
        }`}
      >
        {positive ? '+' : ''}
        {fmtNum(t.change)}
      </td>
      <td className="px-4 py-2 text-right font-mono text-xs text-nurev-text">
        {fmtNum(t.newShares)}
      </td>
      <td className="max-w-[200px] truncate px-4 py-2 text-xs text-nurev-muted">
        {t.reason || '—'}
      </td>
    </tr>
  );
}

// ─── Announcements tab ────────────────────────────────────────────────────

function AnnouncementsTab() {
  // No pagination yet — surface the 25 most recent announcements.
  const page = 1;
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    body: '',
    category: 'general' as AnnouncementCategory,
    pinned: false,
    notifyByEmail: false
  });
  const [status, setStatus] = useState<string | null>(null);

  const { data, isLoading } = useAdminAnnouncements({ page, limit: 25 });
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();
  const del = useDeleteAnnouncement();

  const docs = data?.docs || [];

  const reset = () => {
    setForm({
      title: '',
      body: '',
      category: 'general',
      pinned: false,
      notifyByEmail: false
    });
    setShowForm(false);
    setEditId(null);
    setStatus(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus(null);
    if (!form.title || !form.body) {
      setStatus('Title and body required.');
      return;
    }
    try {
      if (editId) {
        await update.mutateAsync({
          id: editId,
          title: form.title,
          body: form.body,
          category: form.category,
          pinned: form.pinned
        });
        reset();
      } else {
        if (form.notifyByEmail) {
          if (
            !window.confirm(
              'Send this announcement by email to all shareholders?'
            )
          ) {
            return;
          }
        }
        const res = await create.mutateAsync(form);
        const notify = res.notify;
        if (notify?.sent) {
          setStatus(
            `Posted — emailed ${notify.delivered ?? 0}/${notify.recipients ?? 0}${
              notify.failed?.length ? ` (${notify.failed.length} failed)` : ''
            }.`
          );
        }
        reset();
      }
    } catch (err) {
      setStatus(
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Save failed.'
      );
    }
  };

  const startEdit = (a: {
    _id: string;
    title: string;
    body: string;
    category: AnnouncementCategory;
    pinned: boolean;
  }) => {
    setEditId(a._id);
    setForm({
      title: a.title,
      body: a.body,
      category: a.category,
      pinned: a.pinned,
      notifyByEmail: false
    });
    setShowForm(true);
    setStatus(null);
  };

  const onDelete = async (id: string, title: string) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await del.mutateAsync(id);
    } catch (err) {
      window.alert(
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Delete failed.'
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-nurev-muted">
          Updates posted to all shareholders.
        </p>
        <ActionButton
          size="sm"
          onClick={() => {
            reset();
            setShowForm(true);
          }}
        >
          + New Announcement
        </ActionButton>
      </div>

      {showForm && (
        <Card className="border-amber-500/30">
          <form onSubmit={submit} className="space-y-4">
            <SectionLabel>
              {editId ? 'Edit Announcement' : 'New Announcement'}
            </SectionLabel>
            <Field label="Title">
              <Input
                value={form.title}
                onChange={v => setForm({ ...form, title: v })}
              />
            </Field>
            <Field label="Body">
              <textarea
                value={form.body}
                onChange={e => setForm({ ...form, body: e.target.value })}
                rows={5}
                className="w-full resize-y rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <select
                  value={form.category}
                  onChange={e =>
                    setForm({
                      ...form,
                      category: e.target.value as AnnouncementCategory
                    })
                  }
                  className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
                >
                  {CATEGORIES.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="flex items-end">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-nurev-text">
                  <input
                    type="checkbox"
                    checked={form.pinned}
                    onChange={e =>
                      setForm({ ...form, pinned: e.target.checked })
                    }
                    className="accent-amber-500"
                  />
                  Pin to top
                </label>
              </div>
            </div>

            {!editId && (
              <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-3">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={form.notifyByEmail}
                    onChange={e =>
                      setForm({ ...form, notifyByEmail: e.target.checked })
                    }
                    className="mt-0.5 accent-amber-500"
                  />
                  <div>
                    <p className="text-xs font-medium text-nurev-text">
                      Email all shareholders
                    </p>
                    <p className="mt-0.5 text-[10px] text-nurev-muted">
                      Sends to every shareholder past the{' '}
                      <span className="font-mono">pending</span> stage. Edits
                      made later are not re-sent.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {status && (
              <p className="text-xs text-amber-300">{status}</p>
            )}

            <div className="flex gap-2">
              <ActionButton
                type="submit"
                size="sm"
                disabled={create.isPending || update.isPending}
              >
                {create.isPending || update.isPending
                  ? 'Saving…'
                  : editId
                    ? 'Update'
                    : form.notifyByEmail
                      ? 'Post + Email'
                      : 'Post'}
              </ActionButton>
              <ActionButton size="sm" variant="neutral" onClick={reset}>
                Cancel
              </ActionButton>
            </div>
          </form>
        </Card>
      )}

      <Card>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : docs.length === 0 ? (
          <p className="py-8 text-center text-sm text-nurev-muted">
            No announcements yet.
          </p>
        ) : (
          <div className="space-y-3">
            {docs.map(a => (
              <div
                key={a._id}
                className="rounded-lg border border-nurev-border bg-white/[0.03] p-4"
              >
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {a.pinned && (
                      <span className="inline-block rounded bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium text-amber-300 ring-1 ring-amber-500/30">
                        Pinned
                      </span>
                    )}
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${catBadge(a.category)}`}
                    >
                      {catLabel(a.category)}
                    </span>
                    <p className="text-[10px] text-nurev-muted">
                      {fmtDate(a.publishedAt)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(a)}
                      className="text-[10px] text-amber-300 hover:text-amber-200"
                    >
                      edit
                    </button>
                    <button
                      onClick={() => onDelete(a._id, a.title)}
                      className="text-[10px] text-red-300 hover:text-red-200"
                    >
                      delete
                    </button>
                  </div>
                </div>
                <p className="mb-1 text-sm font-medium text-nurev-text">
                  {a.title}
                </p>
                <p className="whitespace-pre-wrap text-xs text-nurev-muted line-clamp-3">
                  {a.body}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Documents tab ────────────────────────────────────────────────────────

function DocumentsTab() {
  // No pagination yet — surface the 25 most recent docs.
  const page = 1;
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    fileUrl: '',
    fileName: ''
  });
  const [status, setStatus] = useState<string | null>(null);

  const { data, isLoading } = useShareholderDocuments({ page, limit: 25 });
  const create = useCreateDocument();
  const del = useDeleteDocument();
  const docs = data?.docs || [];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus(null);
    if (!form.title || !form.fileUrl || !form.fileName) {
      setStatus('Title, URL and filename required.');
      return;
    }
    try {
      await create.mutateAsync(form);
      setForm({ title: '', description: '', fileUrl: '', fileName: '' });
      setShowAdd(false);
    } catch (err) {
      setStatus(
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Add failed.'
      );
    }
  };

  const onDelete = async (d: ShareholderDocument) => {
    if (!window.confirm(`Remove "${d.title}"?`)) return;
    try {
      await del.mutateAsync(d._id);
    } catch (err) {
      window.alert(
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Delete failed.'
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-nurev-muted">
          Documents shared with shareholders.
        </p>
        <ActionButton size="sm" onClick={() => setShowAdd(s => !s)}>
          {showAdd ? 'Cancel' : '+ Add Document'}
        </ActionButton>
      </div>

      {showAdd && (
        <Card className="border-amber-500/30">
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Title">
                <Input
                  value={form.title}
                  onChange={v => setForm({ ...form, title: v })}
                />
              </Field>
              <Field label="File Name">
                <Input
                  value={form.fileName}
                  placeholder="report.pdf"
                  onChange={v => setForm({ ...form, fileName: v })}
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="File URL">
                  <Input
                    value={form.fileUrl}
                    mono
                    placeholder="https://…"
                    onChange={v => setForm({ ...form, fileUrl: v })}
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Description">
                  <Input
                    value={form.description}
                    onChange={v => setForm({ ...form, description: v })}
                  />
                </Field>
              </div>
            </div>
            {status && <p className="text-xs text-red-300">{status}</p>}
            <div className="flex gap-2">
              <ActionButton
                type="submit"
                size="sm"
                disabled={create.isPending}
              >
                {create.isPending ? 'Adding…' : 'Add'}
              </ActionButton>
              <ActionButton
                size="sm"
                variant="neutral"
                onClick={() => setShowAdd(false)}
              >
                Cancel
              </ActionButton>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <div className="space-y-3">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))
          ) : docs.length === 0 ? (
            <p className="py-8 text-center text-sm text-nurev-muted">
              No documents shared yet.
            </p>
          ) : (
            docs.map(d => (
              <div
                key={d._id}
                className="flex items-center justify-between rounded-lg bg-white/[0.03] p-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-nurev-text">{d.title}</p>
                  <p className="text-[10px] text-nurev-muted">
                    {d.fileName} · {fmtDate(d.createdAt)}
                  </p>
                  {d.description && (
                    <p className="mt-0.5 text-[10px] text-nurev-muted">
                      {d.description}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <a
                    href={d.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-amber-300 hover:text-amber-200"
                  >
                    view
                  </a>
                  <button
                    onClick={() => onDelete(d)}
                    className="text-[10px] text-red-300 hover:text-red-200"
                  >
                    remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

// ─── Tiny form atoms ─────────────────────────────────────────────────────

function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({
  value,
  onChange,
  type = 'text',
  placeholder,
  mono
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <input
      value={value}
      type={type}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      className={`w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none ${
        mono ? 'font-mono' : ''
      }`}
    />
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'shareholders', label: 'Shareholders' },
  { id: 'transactions', label: 'Transaction Log' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'documents', label: 'Documents' }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function CapTableScreen() {
  const [tab, setTab] = useState<Tab>('overview');

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Cap Table
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Source of truth for shareholders, share-class allocation, transaction
            audit log, and investor portal access. Every share change is
            audit-logged with a required reason field.
          </p>
        </div>
        <a
          href="https://app.nurev.io/cap-table"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md px-4 py-2 text-xs font-medium transition ${
              tab === t.id
                ? 'bg-amber-500 text-nurev-bg'
                : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'shareholders' && <ShareholdersTab />}
      {tab === 'transactions' && <TransactionsTab />}
      {tab === 'announcements' && <AnnouncementsTab />}
      {tab === 'documents' && <DocumentsTab />}
    </div>
  );
}
